<template>
	<div>
		<h3 class="typo-h4 sm:typo-h5 mb-2" :class="showMobileHeading ? 'md:hidden' : 'hidden'">
			Mapa pobočky
		</h3>
		<Transition name="fade">
			<div
				:key="isFullScreen ? 'fullscreen' : 'windowed'"
				class="isolate relative flex justify-end items-start overflow-hidden bg-neutral-300 select-none"
				:class="[
					{
						[tailwindAspectRatio]: !isFullScreen,
						'!fixed inset-0 m-2 z-popup sm:m-4 md:m-6': isFullScreen,
					},
				]"
				v-bind="$attrs"
			>
				<span v-if="isFullScreen" class="fixed -z-10 inset-0 bg-neutral-300/50" />
				<div class="relative z-10 flex gap-1 mt-2 mr-3">
					<button
						class="btn btn-md btn-icon btn-secondary"
						:disabled="!canZoomIn"
						:aria-disabled="!canZoomIn"
						aria-label="Přiblížit"
						@click.prevent="zoomStep(1)"
					>
						<NuxtIcon name="plus" class="rem:text-[24px]" />
					</button>
					<button
						class="btn btn-md btn-icon btn-secondary"
						:disabled="!canZoomOut"
						:aria-disabled="!canZoomOut"
						aria-label="Oddálit"
						@click.prevent="zoomStep(-1)"
					>
						<NuxtIcon name="minus" class="rem:text-[24px]" />
					</button>
					<button
						class="max-sm:btn-icon btn btn-md btn-secondary gap-2"
						:aria-label="isFullScreen ? 'Zmenšit' : 'Zvětšit'"
						@click="toggleFullScreen"
					>
						<NuxtIcon :name="isFullScreen ? 'custom/windowed' : 'custom/fullscreen'" class="rem:text-[24px]" />
						<span class="max-sm:hidden rem:text-[12px] font-bold" v-text="isFullScreen ? 'Zmenšit' : 'Zvětšit'" />
					</button>
				</div>
				<div
					ref="map"
					:style="styles"
				>
					<ClientOnly>
						<NuxtImg
							:src="map.filename"
							:alt="map.alt || 'Mapa'"
							v-bind="imageProps"
							class="w-full pointer-events-none will-change-transform"
							@load="hideMapLoader"
						/>
					</ClientOnly>
				</div>
				<Transition name="fade">
					<span v-if="!isMapLoaded" class="absolute inset-0 flex justify-center items-center bg-neutral-300">
						<NuxtIcon
							name="custom/loader"
							class="text-icon-primary animate-spin text-3xl"
							filled
						/>
					</span>
				</Transition>
			</div>
		</Transition>
	</div>
</template>

<script setup lang="ts">
	import type {ImageProps} from '@nuxt/image/runtime';
	import type {PageTypeBranchStoryblok} from '~/storyblok/types';

	defineOptions({inheritAttrs: false});

	const TRANSITION_DURATION = 300;

	defineProps<{
		map: PageTypeBranchStoryblok['map'];
		imageProps?: ImageProps<'storyblok'>;
		tailwindAspectRatio: string;
		showMobileHeading?: boolean;
	}>();

	const isMapLoaded = ref<boolean>(false);
	const isFullScreen = ref(false);
	const isScrollLocked = useBodyScrollLock();
	const {width} = useWindowSize();

	const steps = computed(() => {
		return width.value > 560
			? [
				{scale: 1},
				{
					scale: 1.5,
					default: true,
				},
				{scale: 2.5},
			]
			: [
				{scale: 2},
				{
					scale: 3,
					default: true,
				},
				{scale: 5},
			];
	});

	const mapRef = useTemplateRef<HTMLElement>('map');

	const {
		canZoomIn,
		canZoomOut,
		styles,
		zoomStep,
		reset,
	} = useMapControls(
		mapRef,
		{
			steps,
			fill: true,
		},
	);

	watch(isMapLoaded, () => {
		reset();
	});

	watchDebounced(width, () => {
		reset();
	}, {debounce: 300});

	watch(isFullScreen, () => {
		isMapLoaded.value = false;
		isScrollLocked.value = isFullScreen.value;
	});

	const toggleFullScreen = () => {
		isFullScreen.value = !isFullScreen.value;
	};

	const hideMapLoader = () => {
		setTimeout(() => { isMapLoaded.value = true; }, TRANSITION_DURATION);
	};
</script>
